import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Form, notification, Select, Spin } from "antd";
import type { Moment } from "moment";
import moment from "moment";
import Button from "@lhd/components/Button";
import { CommonDateFormPicker } from "@lhd/components/CommonDatePicker";
import ExtendedTimePicker from "@lhd/components/ExtendedTimePicker";
import { DATE_TIME_FORMAT } from "@lhd/constants";
import {
  getWorkerLogtimeApiErrorMessage,
  useFetchWorkerLogtimePermissions,
  useFetchWorkerLogTimesByDate,
  useFetchWorkersWithNoFilter,
  useGetAuth,
  useSaveWorkerLogtimesSave,
} from "@lhd/hooks";
import type { WorkerLogtimesSaveJsonItem } from "@lhd/hooks";
import { LogItem, LogTimeByDateResponse, Worker } from "@lhd/types";
import { BiPlus, BiTrash } from "react-icons/bi";
import {
  formatIsoInTokyo,
  workDateTimeToLoggedAtIso,
} from "@lhd/features/TrackingTime/workLogTimezone";
import { LOG_TYPE } from "@lhd/features/TrackingTime/HomeTracking/contants";
import {
  buildTrackingTimelineRows,
  groupDisplayTimelineRowsIntoValidationCycles,
} from "@lhd/features/TrackingTime/timelineRows";
import { useServerNowMs } from "@lhd/features/TrackingTime/useServerNowMs";
import {
  disabledTimeProps,
  maxTime,
  minTime,
  validateWorkShifts,
} from "./validation";
import { ModalWrapper } from "./styled";
import { CHECK_IN_HOUR_COUNT, EXTENDED_TIME_HOUR_COUNT } from "../../until";

type Props = {
  open: boolean;
  companyId?: number | string;
  onClose: () => void;
  onSaved?: () => void | Promise<void>;
};

type FormValues = {
  workerId: string;
  date: Moment;
  shifts: WorkShift[];
};

type BreakTime = {
  startId?: number;
  start?: string;
  endId?: number;
  end?: string;
};

type WorkShift = {
  checkInId?: number;
  checkIn?: string;
  checkOutId?: number;
  checkOut?: string;
  breaks?: Array<BreakTime | undefined>;
};

type LogtimeInput = {
  id?: number;
  log_type: "check_in" | "break_start" | "break_end" | "check_out";
  logged_at: string;
};

type BaselineLogtime = {
  logType: LogtimeInput["log_type"];
  loggedAt: string;
};

const PUNCH_LOG_TYPES = new Set<LogtimeInput["log_type"]>([
  "check_in",
  "break_start",
  "break_end",
  "check_out",
]);

const isPunchLogType = (logType: LogItem["log_type"]): logType is LOG_TYPE =>
  PUNCH_LOG_TYPES.has(logType as LogtimeInput["log_type"]);

const buildLogtime = (
  workDate: string,
  logType: LogtimeInput["log_type"],
  value?: string,
  id?: number,
): LogtimeInput | null => {
  if (!value) return null;
  const loggedAt = workDateTimeToLoggedAtIso(workDate, value);
  return loggedAt
    ? {
        ...(id != null ? { id } : {}),
        log_type: logType,
        logged_at: loggedAt,
      }
    : null;
};

const toExtendedTime = (
  loggedAt: string,
  workDate: string,
): string | undefined => {
  const time = formatIsoInTokyo(loggedAt, DATE_TIME_FORMAT.TIME);
  const date = formatIsoInTokyo(loggedAt, DATE_TIME_FORMAT.DATE_QUERY);
  const dayOffset = moment(date, DATE_TIME_FORMAT.DATE_QUERY, true).diff(
    moment(workDate, DATE_TIME_FORMAT.DATE_QUERY, true),
    "days",
  );
  const hour = Number(time.slice(0, 2)) + dayOffset * 24;
  if (!Number.isInteger(hour) || hour < 0 || hour >= 100) return undefined;
  return `${String(hour).padStart(2, "0")}:${time.slice(3)}`;
};

const toPickerTime = (log: LogItem | undefined, workDate: string) =>
  log?.logged_at ? toExtendedTime(log.logged_at, workDate) : undefined;

const mapCycleToShift = (cycle: LogItem[], workDate: string): WorkShift => {
  const checkIn = cycle.find((log) => log.log_type === LOG_TYPE.CHECKIN);
  const checkOut = cycle.find((log) => log.log_type === LOG_TYPE.CHECKOUT);
  const breaks: BreakTime[] = [];

  cycle.forEach((log) => {
    if (log.log_type === LOG_TYPE.BREAK_START) {
      breaks.push({
        startId: log.id > 0 ? log.id : undefined,
        start: toPickerTime(log, workDate),
      });
      return;
    }
    if (log.log_type !== LOG_TYPE.BREAK_END) return;

    const unmatchedBreak = [...breaks]
      .reverse()
      .find((breakTime) => breakTime.end == null);
    if (unmatchedBreak) {
      unmatchedBreak.endId = log.id > 0 ? log.id : undefined;
      unmatchedBreak.end = toPickerTime(log, workDate);
    } else {
      breaks.push({
        endId: log.id > 0 ? log.id : undefined,
        end: toPickerTime(log, workDate),
      });
    }
  });

  return {
    checkInId: checkIn && checkIn.id > 0 ? checkIn.id : undefined,
    checkIn: toPickerTime(checkIn, workDate),
    checkOutId: checkOut && checkOut.id > 0 ? checkOut.id : undefined,
    checkOut: toPickerTime(checkOut, workDate),
    breaks: breaks.filter((breakTime) => breakTime.start || breakTime.end),
  };
};

const mapLogtimesToShifts = (data: LogTimeByDateResponse): WorkShift[] => {
  const cycles = groupDisplayTimelineRowsIntoValidationCycles(
    buildTrackingTimelineRows(data),
  );
  const shifts = cycles
    .map((cycle) => mapCycleToShift(cycle, data.date))
    .reverse();
  return shifts.length > 0 ? shifts : [{ breaks: [] }];
};

const getWorkerName = (worker: Worker) =>
  worker.full_name ||
  worker.person?.name ||
  worker.worker_no ||
  String(worker.id);

const AddTrackingModal: FC<Props> = ({ open, companyId, onClose, onSaved }) => {
  const { isAdmin } = useGetAuth();
  const [form] = Form.useForm<FormValues>();
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [saveError, setSaveError] = useState("");
  const hydratedSelectionRef = useRef("");
  const baselineLogtimesRef = useRef(new Map<number, BaselineLogtime>());
  const serverNowMs = useServerNowMs(open);
  const saveMutation = useSaveWorkerLogtimesSave();
  const numericCompanyId = Number(companyId);
  const hasCompanyId =
    Number.isFinite(numericCompanyId) && numericCompanyId > 0;

  const { data: logtimePermissions, isLoading: isLoadingLogtimePermissions } =
    useFetchWorkerLogtimePermissions(!isAdmin);

  const logtimeEdit = logtimePermissions?.logtime_edit || [];

  const { data: workerData, isLoading: isLoadingWorkers } =
    useFetchWorkersWithNoFilter(
      1,
      100000,
      {},
      hasCompanyId ? numericCompanyId : undefined,
      false,
      {
        enabled: open && hasCompanyId,
      },
    );

  const workerOptions = useMemo(
    () =>
      (workerData?.data || [])
        .filter((worker) => isAdmin || logtimeEdit.includes(Number(worker.id)))
        .map((worker) => ({
          value: worker.id,
          label: getWorkerName(worker),
        })),
    [workerData?.data],
  );

  const selectedWorkerId = Form.useWatch("workerId", form);
  const selectedDate = Form.useWatch("date", form) as Moment | undefined;
  const watchedShifts = Form.useWatch("shifts", form) as
    | WorkShift[]
    | undefined;
  const selectedDateValue = selectedDate?.format(DATE_TIME_FORMAT.DATE_QUERY);
  const effectiveNowMs = serverNowMs ?? Date.now();
  const serverDateValue = formatIsoInTokyo(
    new Date(effectiveNowMs).toISOString(),
    DATE_TIME_FORMAT.DATE_QUERY,
  );
  const currentTimeLimit = selectedDateValue
    ? toExtendedTime(new Date(effectiveNowMs).toISOString(), selectedDateValue)
    : undefined;
  const selectedWorkerIdValue = Number(selectedWorkerId);
  const hasLogtimeSelection =
    Boolean(selectedDateValue) &&
    Number.isFinite(selectedWorkerIdValue) &&
    selectedWorkerIdValue > 0;
  const selectionKey = hasLogtimeSelection
    ? `${selectedWorkerIdValue}:${selectedDateValue}`
    : "";

  const {
    data: existingLogtimeData,
    isFetching: isFetchingLogtimes,
    isError: isLogtimeError,
    refetch: refetchExistingLogtimes,
  } = useFetchWorkerLogTimesByDate(
    selectedDateValue,
    hasLogtimeSelection ? selectedWorkerIdValue : undefined,
    { enabled: open && hasLogtimeSelection },
  );

  useEffect(() => {
    hydratedSelectionRef.current = "";
    baselineLogtimesRef.current = new Map();
    setValidationErrors([]);
    setSaveError("");
    form.setFieldsValue({ shifts: [{ breaks: [] }] });
  }, [form, selectionKey]);

  useEffect(() => {
    if (!selectionKey || !existingLogtimeData) return;
    if (hydratedSelectionRef.current === selectionKey) return;
    form.setFieldsValue({
      shifts: mapLogtimesToShifts(existingLogtimeData),
    });
    baselineLogtimesRef.current = new Map(
      (existingLogtimeData.logtimes || [])
        .filter(
          (logtime) =>
            logtime.id > 0 &&
            Boolean(logtime.logged_at) &&
            isPunchLogType(logtime.log_type),
        )
        .map(
          (logtime) =>
            [
              logtime.id,
              {
                logType: logtime.log_type as LogtimeInput["log_type"],
                loggedAt: logtime.logged_at,
              },
            ] as const,
        ),
    );
    hydratedSelectionRef.current = selectionKey;
  }, [existingLogtimeData, form, selectionKey]);

  const handleCancel = () => {
    if (saveMutation.isLoading) return;
    setValidationErrors([]);
    setSaveError("");
    form.resetFields();
    onClose();
  };

  const handleSubmit = async (values: FormValues) => {
    const workDate = values.date.format(DATE_TIME_FORMAT.DATE_QUERY);
    const errors = validateWorkShifts(
      workDate,
      values.shifts,
      serverNowMs ?? Date.now(),
    );
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);
    setSaveError("");

    const logtimes = values.shifts.flatMap((shift) => {
      const shiftLogtimes: Array<LogtimeInput | null> = [
        buildLogtime(workDate, "check_in", shift.checkIn, shift.checkInId),
      ];

      (shift.breaks || []).forEach((breakTime) => {
        if (!breakTime) return;
        shiftLogtimes.push(
          buildLogtime(
            workDate,
            "break_start",
            breakTime.start,
            breakTime.startId,
          ),
          buildLogtime(workDate, "break_end", breakTime.end, breakTime.endId),
        );
      });

      shiftLogtimes.push(
        buildLogtime(workDate, "check_out", shift.checkOut, shift.checkOutId),
      );
      return shiftLogtimes.filter(
        (logtime): logtime is LogtimeInput => logtime !== null,
      );
    });

    const activePersistedIds = new Set<number>();
    const changedOrCreatedLogtimes = logtimes.filter((logtime) => {
      if (logtime.id == null) return true;
      activePersistedIds.add(logtime.id);
      const baseline = baselineLogtimesRef.current.get(logtime.id);
      if (!baseline) return true;
      return (
        baseline.logType !== logtime.log_type ||
        Math.floor(Date.parse(baseline.loggedAt) / 60_000) !==
          Math.floor(Date.parse(logtime.logged_at) / 60_000)
      );
    });
    const deletedLogtimes: WorkerLogtimesSaveJsonItem[] = Array.from(
      baselineLogtimesRef.current.keys(),
    )
      .filter((id) => !activePersistedIds.has(id))
      .map((id) => ({ id, _destroy: true }));
    const logtimesToSave: WorkerLogtimesSaveJsonItem[] = [
      ...changedOrCreatedLogtimes,
      ...deletedLogtimes,
    ];

    if (logtimesToSave.length === 0) {
      notification.info({ message: "変更はありません。" });
      return;
    }

    const payload = {
      company_id: String(numericCompanyId),
      worker_id: Number(values.workerId),
      date: workDate,
      logtimes: logtimesToSave,
    };

    try {
      await saveMutation.mutateAsync(payload);
    } catch (error) {
      setSaveError(
        getWorkerLogtimeApiErrorMessage(
          error,
          "勤怠データを登録できませんでした。",
        ),
      );
      return;
    }

    await Promise.all([refetchExistingLogtimes(), onSaved?.()]).catch(
      () => undefined,
    );
    notification.success({ message: "勤怠データを登録しました。" });
    form.resetFields();
    onClose();
  };

  return (
    <ModalWrapper
      title="新規追加"
      open={open}
      onCancel={handleCancel}
      footer={
        <div className="add-tracking-modal-actions">
          <Button
            label="登録"
            loading={saveMutation.isLoading}
            disabled={isFetchingLogtimes || saveMutation.isLoading}
            onClick={() => form.submit()}
          />
          <Button
            label="キャンセル"
            type="cancel-btn"
            disabled={saveMutation.isLoading}
            onClick={handleCancel}
          />
        </div>
      }
      destroyOnClose
      maskClosable={!saveMutation.isLoading}
      closable={!saveMutation.isLoading}
      centered
      width={720}
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        initialValues={{
          date: moment(serverDateValue, DATE_TIME_FORMAT.DATE_QUERY),
          shifts: [{ breaks: [] }],
        }}
        onFinish={handleSubmit}
        onValuesChange={() => {
          setValidationErrors([]);
          setSaveError("");
        }}
      >
        <Form.Item
          label="社員名"
          name="workerId"
          rules={[{ required: true, message: "社員名を選択してください。" }]}
        >
          <Select
            showSearch
            allowClear
            loading={isLoadingWorkers}
            options={workerOptions}
            optionFilterProp="label"
            placeholder="社員名を選択"
            notFoundContent={
              isLoadingWorkers ? "読み込み中..." : "社員が見つかりません。"
            }
          />
        </Form.Item>

        <Form.Item
          label="日付"
          name="date"
          rules={[{ required: true, message: "日付を選択してください。" }]}
        >
          <CommonDateFormPicker
            displayFormat={DATE_TIME_FORMAT.DATE_JP_MD}
            placeholder="日付を選択"
            allowClear
            disabledDate={(current) =>
              current.format(DATE_TIME_FORMAT.DATE_QUERY) > serverDateValue
            }
          />
        </Form.Item>

        {isLogtimeError && (
          <Alert
            className="add-tracking-load-error"
            type="error"
            showIcon
            message="勤怠データを読み込めませんでした。"
          />
        )}

        {validationErrors.length > 0 && (
          <Alert
            className="add-tracking-validation-errors"
            type="error"
            showIcon
            message={validationErrors.map((error) => (
              <div key={error}>{error}</div>
            ))}
          />
        )}

        {saveError && (
          <Alert
            className="add-tracking-save-error"
            type="error"
            showIcon
            message={saveError}
          />
        )}

        <Spin spinning={isFetchingLogtimes || saveMutation.isLoading}>
          <Form.List name="shifts">
            {(shiftFields, { add: addShift, remove: removeShift }) => (
              <div className="add-tracking-shifts">
                {shiftFields.map((shiftField, shiftIndex) => {
                  const shiftValue = watchedShifts?.[shiftIndex];
                  const previousShift = watchedShifts?.[shiftIndex - 1];
                  const nextShift = watchedShifts?.[shiftIndex + 1];
                  const breakValues = shiftValue?.breaks || [];
                  const allBreakTimes = breakValues.flatMap((breakTime) => [
                    breakTime?.start,
                    breakTime?.end,
                  ]);
                  const firstTimeAfterCheckIn = minTime(
                    shiftValue?.checkOut,
                    ...allBreakTimes,
                    currentTimeLimit,
                  );
                  const lastTimeBeforeCheckOut = maxTime(
                    shiftValue?.checkIn,
                    ...allBreakTimes,
                  );
                  const checkOutMaximum = minTime(
                    nextShift?.checkIn,
                    currentTimeLimit,
                  );
                  const hasOpenBreak = breakValues.some(
                    (breakTime) => breakTime?.start && !breakTime?.end,
                  );

                  return (
                    <section
                      className="add-tracking-shift"
                      key={shiftField.key}
                    >
                      <div className="add-tracking-section-header">
                        <strong>勤務</strong>
                        {shiftFields.length > 1 && (
                          <button
                            type="button"
                            className="add-tracking-remove-button"
                            onClick={() => removeShift(shiftField.name)}
                          >
                            <BiTrash size={18} />
                            削除
                          </button>
                        )}
                      </div>

                      <Form.Item name={[shiftField.name, "checkInId"]} hidden>
                        <input type="hidden" />
                      </Form.Item>
                      <Form.Item name={[shiftField.name, "checkOutId"]} hidden>
                        <input type="hidden" />
                      </Form.Item>

                      <div className="add-tracking-time-fields">
                        <Form.Item
                          label="出勤"
                          name={[shiftField.name, "checkIn"]}
                          rules={[
                            {
                              required: true,
                              message: "出勤時間を入力してください。",
                            },
                          ]}
                        >
                          <ExtendedTimePicker
                            extendedTimeHourCount={CHECK_IN_HOUR_COUNT}
                            disabled={
                              shiftIndex > 0 && !previousShift?.checkOut
                            }
                            disabledTime={() =>
                              disabledTimeProps(
                                previousShift?.checkOut,
                                firstTimeAfterCheckIn,
                                { minimumExclusive: shiftIndex > 0 },
                              )
                            }
                          />
                        </Form.Item>

                        <Form.Item
                          label="退勤"
                          name={[shiftField.name, "checkOut"]}
                        >
                          <ExtendedTimePicker
                            extendedTimeHourCount={EXTENDED_TIME_HOUR_COUNT}
                            disabled={!shiftValue?.checkIn || hasOpenBreak}
                            disabledTime={() =>
                              disabledTimeProps(
                                lastTimeBeforeCheckOut,
                                checkOutMaximum,
                                {
                                  maximumExclusive: Boolean(nextShift?.checkIn),
                                },
                              )
                            }
                          />
                        </Form.Item>
                      </div>

                      <Form.List name={[shiftField.name, "breaks"]}>
                        {(
                          breakFields,
                          { add: addBreak, remove: removeBreak },
                        ) => (
                          <div className="add-tracking-breaks">
                            {breakFields.map((breakField, breakIndex) => {
                              const breakValue = breakValues[breakIndex];
                              const previousBreak = breakValues[breakIndex - 1];
                              const nextBreak = breakValues[breakIndex + 1];
                              const breakStartMinimum = maxTime(
                                shiftValue?.checkIn,
                                previousBreak?.end,
                              );
                              const breakStartMaximum = minTime(
                                breakValue?.end,
                                nextBreak?.start,
                                shiftValue?.checkOut,
                                currentTimeLimit,
                              );
                              const breakEndMaximum = minTime(
                                nextBreak?.start,
                                shiftValue?.checkOut,
                                currentTimeLimit,
                              );

                              return (
                                <div
                                  className="add-tracking-break-row"
                                  key={breakField.key}
                                >
                                  <Form.Item
                                    name={[breakField.name, "startId"]}
                                    hidden
                                  >
                                    <input type="hidden" />
                                  </Form.Item>
                                  <Form.Item
                                    name={[breakField.name, "endId"]}
                                    hidden
                                  >
                                    <input type="hidden" />
                                  </Form.Item>
                                  <span className="add-tracking-break-number">
                                    休憩 {breakIndex + 1}
                                  </span>
                                  <Form.Item
                                    label="休憩開始"
                                    name={[breakField.name, "start"]}
                                  >
                                    <ExtendedTimePicker
                                      extendedTimeHourCount={
                                        EXTENDED_TIME_HOUR_COUNT
                                      }
                                      disabled={
                                        !shiftValue?.checkIn ||
                                        Boolean(
                                          previousBreak?.start &&
                                            !previousBreak?.end,
                                        )
                                      }
                                      disabledTime={() =>
                                        disabledTimeProps(
                                          breakStartMinimum,
                                          breakStartMaximum,
                                        )
                                      }
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    label="休憩終了"
                                    name={[breakField.name, "end"]}
                                  >
                                    <ExtendedTimePicker
                                      extendedTimeHourCount={
                                        EXTENDED_TIME_HOUR_COUNT
                                      }
                                      disabled={!breakValue?.start}
                                      disabledTime={() =>
                                        disabledTimeProps(
                                          breakValue?.start,
                                          breakEndMaximum,
                                        )
                                      }
                                    />
                                  </Form.Item>
                                  <button
                                    type="button"
                                    className="add-tracking-icon-button"
                                    aria-label={`休憩 ${breakIndex + 1} を削除`}
                                    onClick={() => removeBreak(breakField.name)}
                                  >
                                    <BiTrash size={20} />
                                  </button>
                                </div>
                              );
                            })}

                            <button
                              type="button"
                              className="add-tracking-add-button"
                              disabled={!shiftValue?.checkIn || hasOpenBreak}
                              onClick={() => addBreak()}
                            >
                              <BiPlus size={20} />
                              休憩を追加
                            </button>
                          </div>
                        )}
                      </Form.List>
                    </section>
                  );
                })}
              </div>
            )}
          </Form.List>
        </Spin>
      </Form>
    </ModalWrapper>
  );
};

export default AddTrackingModal;
